import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2, TrendingUp, Target, Award, Users, Zap, Mail, Calendar, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { ReportFooter } from "@/components/ReportFooter";
import { useLanguageWithOverride, Language } from "@/hooks/use-browser-language";
import { getThankYouTranslation } from "@/i18n/social-selling-thankyou";

const SocialSellingThankYou = () => {
  const { language, setLanguage } = useLanguageWithOverride();

  // Translation helper
  const t = (key: Parameters<typeof getThankYouTranslation>[1]) => 
    getThankYouTranslation(language, key);

  const toggleLanguage = () => {
    const newLang: Language = language === "pl" ? "en" : "pl";
    setLanguage(newLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", newLang);
    window.history.replaceState({}, "", url.toString());
  };

  const heroStats = [
    {
      value: t("stat1Value"),
      label: t("stat1Label"),
      icon: <TrendingUp className="h-8 w-8 md:h-10 md:w-10" />,
    },
    {
      value: t("stat2Value"),
      label: t("stat2Label"),
      icon: <Target className="h-8 w-8 md:h-10 md:w-10" />,
    },
    {
      value: t("stat3Value"),
      label: t("stat3Label"),
      icon: <Award className="h-8 w-8 md:h-10 md:w-10" />,
    },
    {
      value: t("stat4Value"),
      label: t("stat4Label"),
      icon: <Users className="h-8 w-8 md:h-10 md:w-10" />,
    },
  ];

  const detailedResults = [
    {
      title: t("result1Title"),
      mainStat: t("result1MainStat"),
      bullets: [
        t("result1Bullet1"),
        t("result1Bullet2"),
        t("result1Bullet3"),
      ],
      gradient: "from-primary/20 via-primary/10 to-background",
      icon: <Users className="h-12 w-12 text-primary" />,
    },
    {
      title: t("result2Title"),
      mainStat: t("result2MainStat"),
      bullets: [
        t("result2Bullet1"),
        t("result2Bullet2"),
        t("result2Bullet3"),
      ],
      gradient: "from-amber-500/20 via-amber-500/10 to-background",
      icon: <Award className="h-12 w-12 text-amber-600" />,
    },
    {
      title: t("result3Title"),
      mainStat: t("result3MainStat"),
      bullets: [
        t("result3Bullet1"),
        t("result3Bullet2"),
        t("result3Bullet3"),
      ],
      gradient: "from-emerald-500/20 via-emerald-500/10 to-background",
      icon: <TrendingUp className="h-12 w-12 text-emerald-600" />,
    },
  ];

  const nextSteps = [
    {
      icon: <Mail className="h-6 w-6 text-primary" />,
      title: t("nextStep1Title"),
      description: t("nextStep1Desc"),
    },
    {
      icon: <Calendar className="h-6 w-6 text-primary" />,
      title: t("nextStep2Title"),
      description: t("nextStep2Desc"),
    },
    {
      icon: <Zap className="h-6 w-6 text-primary" />,
      title: t("nextStep3Title"),
      description: t("nextStep3Desc"),
    },
  ];

  return (
    <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-8 md:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-6xl mx-auto space-y-8 md:space-y-12 pb-16">
            
            {/* Language Switcher */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                className="flex items-center gap-2"
              >
                <Globe className="h-4 w-4" />
                <span className="font-medium">{language === "pl" ? "EN" : "PL"}</span>
              </Button>
            </div>

            {/* Hero Section */}
            <div className="text-center animate-fade-in">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 mb-6 animate-scale-in">
                <CheckCircle2 className="h-8 w-8 md:h-10 md:w-10 text-primary" />
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
                {t("heroTitle")}
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-4">
                {t("heroSubtitle")}
              </p>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                {t("heroDescription")} <strong>{t("heroTimeframe")}</strong>
              </p>
            </div>

            {/* Hero Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {heroStats.map((stat, index) => (
                <Card 
                  key={index} 
                  className="border-none shadow-lg bg-gradient-to-br from-primary/20 via-primary/10 to-background hover:shadow-xl hover:scale-105 transition-all animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-4 md:p-6 text-center">
                    <div className="flex justify-center mb-3 md:mb-4 text-primary">
                      {stat.icon}
                    </div>
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-2 md:mb-3">
                      {stat.value}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                      {stat.label}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Detailed Results Section */}
            <div className="space-y-4 md:space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
                  {t("resultsTitle")}
                </h2>
                <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                  {t("resultsSubtitle")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {detailedResults.map((result, index) => (
                  <Card 
                    key={index}
                    className="border-none shadow-xl overflow-hidden animate-fade-in hover:shadow-2xl transition-all"
                    style={{ animationDelay: `${(index + 4) * 100}ms` }}
                  >
                    <div className={`h-3 bg-gradient-to-r ${result.gradient}`} />
                    <CardHeader className="pb-4 bg-gradient-to-br from-secondary/30 to-background">
                      <div className="flex items-start gap-4 mb-3">
                        <div className="flex-shrink-0 bg-background/80 p-2 rounded-lg">
                          {result.icon}
                        </div>
                        <CardTitle className="text-lg md:text-xl leading-tight">
                          {result.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-base md:text-lg font-semibold text-foreground">
                        {result.mainStat}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 bg-gradient-to-br from-secondary/10 to-background">
                      {result.bullets.map((bullet, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm md:text-base text-muted-foreground">
                            {bullet}
                          </span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Next Steps Section */}
            <Card className="gradient-card border-none shadow-xl">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl md:text-3xl mb-3">{t("nextStepsTitle")}</CardTitle>
                <CardDescription className="text-base md:text-lg">
                  {t("nextStepsSubtitle")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {nextSteps.map((step, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 md:p-6 rounded-xl bg-gradient-to-br from-secondary/50 to-secondary/30 hover:shadow-md transition-all"
                  >
                    <div className="flex-shrink-0 bg-primary/10 p-3 rounded-lg">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-base md:text-lg mb-1">{step.title}</h3>
                      <p className="text-sm md:text-base text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="gradient-hero border-none shadow-2xl text-white overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent" />
              <CardContent className="p-8 md:p-12 text-center relative z-10">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                  {t("ctaTitle")}
                </h2>
                <p className="text-base md:text-lg mb-8 opacity-90 max-w-2xl mx-auto">
                  {t("ctaSubtitle")}
                </p>
                <Button 
                  asChild
                  size="lg" 
                  className="bg-white text-primary hover:bg-white/90 shadow-xl px-8 py-6 h-auto"
                >
                  <Link to="/">
                    {t("ctaButton")}
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Sources */}
            <div className="text-center text-sm text-muted-foreground pt-4 pb-8">
              <p>
                {t("sources")}
              </p>
            </div>

          </div>
        </div>
      </div>
      <ReportFooter />
    </ScrollArea>
  );
};

export default SocialSellingThankYou;
